// src/admin/AllAttendanceManager.js - Quản lý tất cả sự kiện điểm danh thiếu nhi (Thêm, Sửa, Xóa, Lọc từng cột, Sidebar)
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteField,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { StudentContext } from '../StudentContext';
import * as XLSX from 'xlsx';
import './AllAttendanceManager.css';

const AllAttendanceManager = () => {
  const { currentYearId: contextYearId, classMap } = useContext(StudentContext);
  const outletContext = useOutletContext() || {};
  const sidebarSelectedClass = outletContext.selectedClassForOutlet || 'all';

  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [loading, setLoading] = useState(true);

  // Danh sách sự kiện điểm danh & Danh sách học sinh phục vụ modal Thêm
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allStudentsLookup, setAllStudentsLookup] = useState([]);

  // Bộ lọc từng cột
  const [colFilters, setColFilters] = useState({
    studentId: '',
    name: '',
    className: '',
    activityType: 'ALL',
    date: '',
    status: '',
    catechist: ''
  });

  // Sắp xếp
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Phân trang / Giới hạn hiển thị
  const [displayLimit, setDisplayLimit] = useState(60);

  // 🌟 State Modal THÊM MỚI (Có lọc theo lớp & gợi ý tìm kiếm)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addFilterClass, setAddFilterClass] = useState('ALL');
  const [studentSearchText, setStudentSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addForm, setAddForm] = useState({
    studentId: '',
    activityType: 'church', // 'church' | 'catechism'
    date: new Date().toLocaleDateString('sv-SE'),
    timeStatus: 'Có lúc 07:15',
    catechist: 'Admin'
  });
  const [isAdding, setIsAdding] = useState(false);

  // State Modal CHỈNH SỬA
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    activityType: 'church',
    date: '',
    timeStatus: '',
    catechist: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  // State Xóa
  const [deletingKey, setDeletingKey] = useState(null);

  // 1. Tải dữ liệu Niên Khóa & Nhật ký Điểm danh từ Firestore
  const fetchAllAttendanceData = async () => {
    setLoading(true);
    try {
      let yearId = contextYearId;
      let yearName = '';

      if (!yearId) {
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) currentYearDoc = allYearsSnap.docs[0];
        }

        if (currentYearDoc) {
          yearId = currentYearDoc.id;
          yearName = currentYearDoc.data().name || yearId;
        }
      }

      if (!yearId) {
        setLoading(false);
        return;
      }

      setCurrentYearId(yearId);
      setCurrentYearName(yearName);

      const [studentsSnap, classesSnap, enrollmentsSnap, attendancesSnap] = await Promise.all([
        getDocs(collection(firestore, 'students')),
        getDocs(collection(firestore, `academic_years/${yearId}/classes`)),
        getDocs(collection(firestore, `academic_years/${yearId}/enrollments`)),
        getDocs(collection(firestore, `academic_years/${yearId}/student_attendances`))
      ]);

      const classesObj = {};
      classesSnap.docs.forEach(d => {
        classesObj[d.id] = d.data().name || d.id;
      });

      const studentsObj = {};
      studentsSnap.docs.forEach(d => {
        studentsObj[d.id] = d.data();
      });

      const enrollmentsObj = {};
      enrollmentsSnap.docs.forEach(d => {
        const data = d.data();
        const sId = data.studentId || d.id;
        enrollmentsObj[sId] = {
          classId: data.classId || '',
          className: classesObj[data.classId] || data.classId || 'Chưa xếp lớp'
        };
      });

      // Tạo danh sách tổng học sinh cho Modal Thêm
      const lookupList = [];
      Object.keys(studentsObj).forEach(sId => {
        const p = studentsObj[sId];
        const enr = enrollmentsObj[sId] || { classId: '', className: 'Chưa xếp lớp' };
        lookupList.push({
          id: sId,
          holyName: p.holyName || '',
          fullName: p.fullName || sId,
          classId: enr.classId,
          className: enr.className
        });
      });
      setAllStudentsLookup(lookupList.sort((a, b) => a.fullName.localeCompare(b.fullName, 'vi')));

      // Phân rã sự kiện điểm danh thành mảng danh sách phẳng
      const recordsList = [];

      attendancesSnap.docs.forEach(docSnap => {
        const studentId = docSnap.id;
        const data = docSnap.data();

        const profile = studentsObj[studentId] || {};
        const enr = enrollmentsObj[studentId] || { classId: '', className: 'Chưa xếp lớp' };

        // Đi Lễ (church)
        const churchMap = data.church || {};
        Object.keys(churchMap).forEach(dateStr => {
          const item = churchMap[dateStr];
          if (item) {
            recordsList.push({
              key: `${studentId}-church-${dateStr}`,
              studentId,
              holyName: profile.holyName || '',
              fullName: profile.fullName || studentId,
              classId: enr.classId,
              className: enr.className,
              activityType: 'church',
              date: dateStr,
              timeStatus: typeof item === 'string' ? item : item.time || 'Có mặt',
              catechist: typeof item === 'object' ? (item.catechist || 'Tự động') : 'Tự động',
              timeLog: typeof item === 'object' ? (item.time_log || '') : ''
            });
          }
        });

        // Học Giáo Lý (catechism)
        const catechismMap = data.catechism || {};
        Object.keys(catechismMap).forEach(dateStr => {
          const item = catechismMap[dateStr];
          if (item) {
            recordsList.push({
              key: `${studentId}-catechism-${dateStr}`,
              studentId,
              holyName: profile.holyName || '',
              fullName: profile.fullName || studentId,
              classId: enr.classId,
              className: enr.className,
              activityType: 'catechism',
              date: dateStr,
              timeStatus: typeof item === 'string' ? item : item.time || 'Có mặt',
              catechist: typeof item === 'object' ? (item.catechist || 'Tự động') : 'Tự động',
              timeLog: typeof item === 'object' ? (item.time_log || '') : ''
            });
          }
        });
      });

      setAttendanceRecords(recordsList);
    } catch (err) {
      console.error('Lỗi nạp tất cả dữ liệu điểm danh:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAttendanceData();
  }, [contextYearId]);

  // Danh sách các lớp khả dụng phục vụ bộ lọc của modal Thêm
  const availableClassesForAdd = useMemo(() => {
    const classSet = new Map();
    if (classMap && Object.keys(classMap).length > 0) {
      Object.entries(classMap).forEach(([id, name]) => {
        classSet.set(id, name);
      });
    } else {
      allStudentsLookup.forEach((s) => {
        if (s.classId && !classSet.has(s.classId)) {
          classSet.set(s.classId, s.className || s.classId);
        }
      });
    }
    return Array.from(classSet.entries()).map(([id, name]) => ({ id, name }));
  }, [classMap, allStudentsLookup]);

  // 🌟 Danh sách gợi ý thiếu nhi dựa trên lớp được lọc và từ khóa nhập vào
  const studentSuggestions = useMemo(() => {
    if (!isAddModalOpen) return [];
    let list = allStudentsLookup;

    // Lọc theo lớp
    if (addFilterClass !== 'ALL') {
      list = list.filter((s) => s.classId === addFilterClass);
    }

    // Lọc theo từ khóa người dùng nhập vào ô input
    if (studentSearchText.trim()) {
      const q = studentSearchText.toLowerCase().trim();
      list = list.filter(
        (s) =>
          (s.id && s.id.toLowerCase().includes(q)) ||
          (s.fullName && s.fullName.toLowerCase().includes(q)) ||
          (s.holyName && s.holyName.toLowerCase().includes(q)) ||
          (s.className && s.className.toLowerCase().includes(q))
      );
    }

    // Giới hạn 25 kết quả gợi ý để danh sách nhẹ và mượt mà
    return list.slice(0, 25);
  }, [isAddModalOpen, allStudentsLookup, addFilterClass, studentSearchText]);

  // Thông tin thiếu nhi đang được chọn trong modal Thêm
  const selectedStudentObj = useMemo(() => {
    if (!addForm.studentId) return null;
    return allStudentsLookup.find((s) => s.id === addForm.studentId) || null;
  }, [addForm.studentId, allStudentsLookup]);

  // 2. Lọc dữ liệu theo Sidebar + Bộ lọc từng cột
  const filteredAndSortedRecords = useMemo(() => {
    let list = attendanceRecords.filter((rec) => {
      if (sidebarSelectedClass !== 'all' && rec.classId !== sidebarSelectedClass) {
        return false;
      }
      if (colFilters.studentId && !rec.studentId.toLowerCase().includes(colFilters.studentId.toLowerCase().trim())) {
        return false;
      }
      if (colFilters.name) {
        const full = `${rec.holyName} ${rec.fullName}`.toLowerCase();
        if (!full.includes(colFilters.name.toLowerCase().trim())) return false;
      }
      if (colFilters.className && !rec.className.toLowerCase().includes(colFilters.className.toLowerCase().trim())) {
        return false;
      }
      if (colFilters.activityType !== 'ALL' && rec.activityType !== colFilters.activityType) {
        return false;
      }
      if (colFilters.date && !rec.date.includes(colFilters.date.trim())) {
        return false;
      }
      if (colFilters.status && !rec.timeStatus.toLowerCase().includes(colFilters.status.toLowerCase().trim())) {
        return false;
      }
      if (colFilters.catechist && !rec.catechist.toLowerCase().includes(colFilters.catechist.toLowerCase().trim())) {
        return false;
      }
      return true;
    });

    list.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'fullName') {
        const getLastName = (name) => name.trim().split(/\s+/).pop().toLowerCase();
        valA = getLastName(a.fullName);
        valB = getLastName(b.fullName);
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [attendanceRecords, sidebarSelectedClass, colFilters, sortField, sortDir]);

  const visibleRecords = useMemo(() => {
    return filteredAndSortedRecords.slice(0, displayLimit);
  }, [filteredAndSortedRecords, displayLimit]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // ====================================================================
  // THÊM LƯỢT ĐIỂM DANH MỚI
  // ====================================================================
  const handleOpenAddModal = () => {
    const defaultClass = sidebarSelectedClass !== 'all' ? sidebarSelectedClass : 'ALL';
    setAddFilterClass(defaultClass);
    setStudentSearchText('');
    setShowSuggestions(false);
    setAddForm({
      studentId: '',
      activityType: 'church',
      date: new Date().toLocaleDateString('sv-SE'),
      timeStatus: 'Có lúc 07:15',
      catechist: 'Admin'
    });
    setIsAddModalOpen(true);
  };

  const handleSelectStudentSuggestion = (student) => {
    setAddForm((prev) => ({ ...prev, studentId: student.id }));
    setStudentSearchText(`${student.holyName ? student.holyName + ' ' : ''}${student.fullName}`);
    setShowSuggestions(false);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!currentYearId || !addForm.date) {
      alert('Vui lòng điền đầy đủ ngày điểm danh!');
      return;
    }

    if (!addForm.studentId) {
      alert('Vui lòng chọn thiếu nhi từ danh sách gợi ý!');
      return;
    }

    setIsAdding(true);
    try {
      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, addForm.studentId);
      const fieldPath = `${addForm.activityType}.${addForm.date}`;

      const payload = {
        time: addForm.timeStatus,
        catechist: addForm.catechist || 'Admin',
        time_log: new Date().toISOString()
      };

      await updateDoc(docRef, {
        [fieldPath]: payload
      }).catch(async () => {
        await setDoc(
          docRef,
          {
            [addForm.activityType]: {
              [addForm.date]: payload
            }
          },
          { merge: true }
        );
      });

      alert('🎉 Đã thêm lượt điểm danh thành công!');
      setIsAddModalOpen(false);
      fetchAllAttendanceData();
    } catch (err) {
      console.error('Lỗi khi thêm điểm danh:', err);
      alert('❌ Lỗi: ' + err.message);
    } finally {
      setIsAdding(false);
    }
  };

  // ====================================================================
  // CHỈNH SỬA LƯỢT ĐIỂM DANH
  // ====================================================================
  const handleOpenEditModal = (rec) => {
    setEditingRecord(rec);
    setEditForm({
      activityType: rec.activityType,
      date: rec.date,
      timeStatus: rec.timeStatus,
      catechist: rec.catechist
    });
    setIsEditModalOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editingRecord || !currentYearId) return;

    setIsEditing(true);
    try {
      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, editingRecord.studentId);
      const isKeyChanged = editingRecord.activityType !== editForm.activityType || editingRecord.date !== editForm.date;

      if (isKeyChanged) {
        await updateDoc(docRef, {
          [`${editingRecord.activityType}.${editingRecord.date}`]: deleteField()
        });
      }

      const newFieldPath = `${editForm.activityType}.${editForm.date}`;
      const payload = {
        time: editForm.timeStatus,
        catechist: editForm.catechist || 'Admin',
        time_log: editingRecord.timeLog || new Date().toISOString()
      };

      await updateDoc(docRef, {
        [newFieldPath]: payload
      });

      alert('🎉 Đã cập nhật lượt điểm danh thành công!');
      setIsEditModalOpen(false);
      setEditingRecord(null);
      fetchAllAttendanceData();
    } catch (err) {
      console.error('Lỗi sửa điểm danh:', err);
      alert('❌ Lỗi khi sửa: ' + err.message);
    } finally {
      setIsEditing(false);
    }
  };

  // ====================================================================
  // XÓA LƯỢT ĐIỂM DANH
  // ====================================================================
  const handleDeleteRecord = async (rec) => {
    if (!currentYearId) return;

    const activityName = rec.activityType === 'church' ? 'Đi Lễ Chúa Nhật' : 'Học Giáo Lý';
    const confirmMsg = `Xác nhận XÓA lượt điểm danh này?\n\n• Thiếu nhi: ${rec.holyName} ${rec.fullName} (${rec.studentId})\n• Lớp: ${rec.className}\n• Hoạt động: ${activityName}\n• Ngày: ${rec.date}\n• Trạng thái: ${rec.timeStatus}`;

    if (!window.confirm(confirmMsg)) return;

    setDeletingKey(rec.key);
    try {
      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, rec.studentId);
      const fieldPath = `${rec.activityType}.${rec.date}`;

      await updateDoc(docRef, {
        [fieldPath]: deleteField()
      });

      setAttendanceRecords((prev) => prev.filter((item) => item.key !== rec.key));
      alert('🗑️ Đã xóa lượt điểm danh thành công!');
    } catch (err) {
      console.error('Lỗi xóa điểm danh:', err);
      alert('❌ Lỗi: ' + err.message);
    } finally {
      setDeletingKey(null);
    }
  };

  // Xuất Excel
  const handleExportExcel = () => {
    const data = [
      ['STT', 'Mã TN', 'Tên Thánh', 'Họ và Tên', 'Lớp', 'Hoạt Động', 'Ngày', 'Trạng Thái / Giờ', 'GLV Quét']
    ];

    filteredAndSortedRecords.forEach((r, idx) => {
      data.push([
        idx + 1,
        r.studentId,
        r.holyName,
        r.fullName,
        r.className,
        r.activityType === 'church' ? 'Đi Lễ' : 'Học Giáo Lý',
        r.date,
        r.timeStatus,
        r.catechist
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'TatCaDiemDanh');
    XLSX.writeFile(wb, `TatCaDiemDanh_${currentYearId || 'NienKhoa'}.xlsx`);
  };

  return (
    <div className="aam-container">
      {/* HEADER */}
      <div className="aam-header">
        <div>
          <h2 className="aam-title">⛪ Quản Lý Tất Cả Sự Kiện Điểm Danh Thiếu Nhi</h2>
          <p className="aam-subtitle">
            Toàn quyền Thêm, Sửa, Xóa và Lọc chi tiết mọi lượt điểm danh Đi Lễ và Học Giáo Lý trong Niên khóa.
          </p>
        </div>
        <div className="aam-header-actions">
          <button onClick={handleOpenAddModal} className="aam-btn aam-btn-add">
            ➕ Thêm Lượt Điểm Danh
          </button>
          <button onClick={handleExportExcel} className="aam-btn aam-btn-excel">
            📥 Xuất Excel ({filteredAndSortedRecords.length})
          </button>
        </div>
      </div>

      {/* THANH THỐNG KÊ NHANH */}
      <div className="aam-stats-banner">
        <div>
          📌 Lớp đang chọn trên Sidebar: <strong>{sidebarSelectedClass === 'all' ? 'Tất cả các lớp' : (classMap[sidebarSelectedClass] || sidebarSelectedClass)}</strong>
        </div>
        <div>
          Tổng số lượt tìm thấy: <strong>{filteredAndSortedRecords.length}</strong> lượt
        </div>
      </div>

      {/* BẢNG DỮ LIỆU ĐIỂM DANH CÓ BỘ LỌC TỪNG CỘT */}
      <div className="aam-table-wrapper">
        <table className="aam-table">
          <thead>
            <tr className="aam-th-main-row">
              <th style={{ width: '45px' }} className="aam-text-center">STT</th>
              <th onClick={() => handleSort('studentId')} className="aam-sortable">
                Mã TN{getSortIcon('studentId')}
              </th>
              <th onClick={() => handleSort('fullName')} className="aam-sortable">
                Họ và Tên{getSortIcon('fullName')}
              </th>
              <th onClick={() => handleSort('className')} className="aam-sortable">
                Lớp{getSortIcon('className')}
              </th>
              <th onClick={() => handleSort('activityType')} className="aam-sortable aam-text-center">
                Hoạt Động{getSortIcon('activityType')}
              </th>
              <th onClick={() => handleSort('date')} className="aam-sortable aam-text-center">
                Ngày{getSortIcon('date')}
              </th>
              <th onClick={() => handleSort('timeStatus')} className="aam-sortable">
                Trạng Thái / Giờ Quét{getSortIcon('timeStatus')}
              </th>
              <th onClick={() => handleSort('catechist')} className="aam-sortable">
                Người Điểm Danh{getSortIcon('catechist')}
              </th>
              <th className="aam-text-center" style={{ width: '130px' }}>Thao Tác</th>
            </tr>

            {/* HÀNG BỘ LỌC TỪNG CỘT */}
            <tr className="aam-th-filter-row">
              <td></td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc mã..."
                  value={colFilters.studentId}
                  onChange={(e) => setColFilters({ ...colFilters, studentId: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc họ tên..."
                  value={colFilters.name}
                  onChange={(e) => setColFilters({ ...colFilters, name: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc lớp..."
                  value={colFilters.className}
                  onChange={(e) => setColFilters({ ...colFilters, className: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td className="aam-text-center">
                <select
                  value={colFilters.activityType}
                  onChange={(e) => setColFilters({ ...colFilters, activityType: e.target.value })}
                  className="aam-col-filter-select"
                >
                  <option value="ALL">Tất cả</option>
                  <option value="church">⛪ Đi Lễ</option>
                  <option value="catechism">📖 Giáo Lý</option>
                </select>
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc ngày..."
                  value={colFilters.date}
                  onChange={(e) => setColFilters({ ...colFilters, date: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc trạng thái..."
                  value={colFilters.status}
                  onChange={(e) => setColFilters({ ...colFilters, status: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Lọc người điểm danh..."
                  value={colFilters.catechist}
                  onChange={(e) => setColFilters({ ...colFilters, catechist: e.target.value })}
                  className="aam-col-filter-input"
                />
              </td>
              <td className="aam-text-center">
                {(colFilters.studentId || colFilters.name || colFilters.className || colFilters.activityType !== 'ALL' || colFilters.date || colFilters.status || colFilters.catechist) && (
                  <button
                    onClick={() => setColFilters({ studentId: '', name: '', className: '', activityType: 'ALL', date: '', status: '', catechist: '' })}
                    className="aam-btn-reset-filters"
                    title="Xóa tất cả bộ lọc"
                  >
                    ✕ Xóa lọc
                  </button>
                )}
              </td>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="aam-empty-td">
                  ⏳ Đang tải toàn bộ dữ liệu điểm danh...
                </td>
              </tr>
            ) : visibleRecords.length === 0 ? (
              <tr>
                <td colSpan="9" className="aam-empty-td">
                  Không tìm thấy lượt điểm danh nào phù hợp với bộ lọc hiện tại.
                </td>
              </tr>
            ) : (
              visibleRecords.map((rec, index) => {
                const isChurch = rec.activityType === 'church';
                const isDeleting = deletingKey === rec.key;

                return (
                  <tr key={rec.key} className="aam-tr">
                    <td className="aam-text-center">{index + 1}</td>
                    <td className="aam-id-code">{rec.studentId}</td>
                    <td>
                      <span className="aam-holy-name">{rec.holyName}</span>
                      <strong>{rec.fullName}</strong>
                    </td>
                    <td>
                      <span className="aam-class-tag">{rec.className}</span>
                    </td>
                    <td className="aam-text-center">
                      <span className={`aam-activity-tag ${isChurch ? 'church' : 'catechism'}`}>
                        {isChurch ? '⛪ Đi Lễ' : '📖 Học Giáo Lý'}
                      </span>
                    </td>
                    <td className="aam-text-center" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {rec.date}
                    </td>
                    <td>
                      <span className={`aam-status-badge ${rec.timeStatus.startsWith('Có lúc') ? 'present' : rec.timeStatus.includes('phép') ? 'excused' : 'unexcused'}`}>
                        {rec.timeStatus}
                      </span>
                    </td>
                    <td>{rec.catechist}</td>
                    <td className="aam-text-center">
                      <div className="aam-row-actions">
                        <button
                          onClick={() => handleOpenEditModal(rec)}
                          className="aam-btn-row-edit"
                          title="Sửa lượt điểm danh"
                        >
                          ✏️ Sửa
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(rec)}
                          disabled={isDeleting}
                          className="aam-btn-row-delete"
                          title="Xóa lượt điểm danh"
                        >
                          {isDeleting ? '⏳' : '🗑️ Xóa'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* FOOTER & LAZY LOAD */}
      {!loading && filteredAndSortedRecords.length > displayLimit && (
        <div className="aam-footer-bar">
          <span>
            Đang hiển thị <strong>{displayLimit}</strong> / <strong>{filteredAndSortedRecords.length}</strong> lượt điểm danh
          </span>
          <button onClick={() => setDisplayLimit((prev) => prev + 60)} className="aam-btn-load-more">
            👇 Tải thêm 60 dòng tiếp theo
          </button>
        </div>
      )}

      {/* 🌟 MODAL THÊM LƯỢT ĐIỂM DANH MỚI (LỌC THEO LỚP & GỢI Ý AUTOCOMPLETE) 🌟 */}
      {isAddModalOpen && (
        <div className="aam-modal-overlay">
          <div className="aam-modal-card">
            <h3 className="aam-modal-title">➕ Thêm Lượt Điểm Danh Thiếu Nhi</h3>
            <form onSubmit={handleSubmitAdd}>
              {/* 1. Bộ lọc lớp */}
              <div className="aam-form-group">
                <label>1. Lọc theo Lớp học:</label>
                <select
                  value={addFilterClass}
                  onChange={(e) => {
                    setAddFilterClass(e.target.value);
                    setShowSuggestions(true);
                  }}
                  className="aam-modal-input"
                >
                  <option value="ALL">-- Tất cả các lớp ({availableClassesForAdd.length} lớp) --</option>
                  {availableClassesForAdd.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Ô input tìm kiếm & Danh sách gợi ý */}
              <div className="aam-form-group">
                <label>2. Tìm & Chọn Thiếu Nhi (*):</label>
                <div className="aam-autocomplete-wrapper">
                  <input
                    type="text"
                    placeholder="Gõ mã ID, tên thánh hoặc họ tên để gợi ý..."
                    value={studentSearchText}
                    onChange={(e) => {
                      setStudentSearchText(e.target.value);
                      setShowSuggestions(true);
                      if (addForm.studentId) {
                        setAddForm((prev) => ({ ...prev, studentId: '' }));
                      }
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 250)}
                    className="aam-modal-input"
                  />

                  {/* Menu gợi ý xổ xuống */}
                  {showSuggestions && (
                    <div className="aam-suggestions-dropdown">
                      {studentSuggestions.length === 0 ? (
                        <div className="aam-suggestion-empty">
                          Không tìm thấy thiếu nhi nào khớp với từ khóa.
                        </div>
                      ) : (
                        studentSuggestions.map((s) => (
                          <div
                            key={s.id}
                            className={`aam-suggestion-item ${addForm.studentId === s.id ? 'active' : ''}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectStudentSuggestion(s);
                            }}
                          >
                            <span className="aam-sugg-id">{s.id}</span>
                            <span className="aam-sugg-name">
                              {s.holyName ? `${s.holyName} ` : ''}
                              <strong>{s.fullName}</strong>
                            </span>
                            <span className="aam-sugg-class">{s.className}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Hộp xác nhận học sinh đã chọn */}
                {selectedStudentObj && (
                  <div className="aam-selected-student-box">
                    <div className="aam-selected-student-info">
                      <span className="aam-selected-icon">✓</span>
                      <span>
                        Đã chọn: <strong>{selectedStudentObj.holyName} {selectedStudentObj.fullName}</strong> ({selectedStudentObj.id}) - Lớp: <em>{selectedStudentObj.className}</em>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAddForm((prev) => ({ ...prev, studentId: '' }));
                        setStudentSearchText('');
                        setShowSuggestions(true);
                      }}
                      className="aam-btn-clear-selection"
                      title="Chọn lại thiếu nhi khác"
                    >
                      ✕ Đổi
                    </button>
                  </div>
                )}
              </div>

              {/* 3. Loại hoạt động */}
              <div className="aam-form-group">
                <label>Loại hoạt động (*):</label>
                <select
                  value={addForm.activityType}
                  onChange={(e) => setAddForm({ ...addForm, activityType: e.target.value })}
                  className="aam-modal-input"
                >
                  <option value="church">⛪ Đi Lễ Chúa Nhật</option>
                  <option value="catechism">📖 Học Giáo Lý</option>
                </select>
              </div>

              {/* 4. Ngày điểm danh */}
              <div className="aam-form-group">
                <label>Ngày điểm danh (*):</label>
                <input
                  type="date"
                  value={addForm.date}
                  onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                  className="aam-modal-input"
                  required
                />
              </div>

              {/* 5. Trạng thái / Giờ quét */}
              <div className="aam-form-group">
                <label>Trạng thái / Giờ quét (*):</label>
                <input
                  type="text"
                  value={addForm.timeStatus}
                  onChange={(e) => setAddForm({ ...addForm, timeStatus: e.target.value })}
                  className="aam-modal-input"
                  placeholder="Ví dụ: Có lúc 07:15, Vắng có phép, Vắng không phép..."
                  required
                />
              </div>

              {/* 6. Người điểm danh */}
              <div className="aam-form-group">
                <label>Người điểm danh (GLV/Admin):</label>
                <input
                  type="text"
                  value={addForm.catechist}
                  onChange={(e) => setAddForm({ ...addForm, catechist: e.target.value })}
                  className="aam-modal-input"
                  placeholder="Nhập tên người điểm danh..."
                />
              </div>

              <div className="aam-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={isAdding}
                  className="aam-btn aam-btn-cancel"
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="aam-btn aam-btn-save"
                >
                  {isAdding ? '⏳ Đang lưu...' : '💾 Thêm Lượt Điểm Danh'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL SỬA LƯỢT ĐIỂM DANH */}
      {isEditModalOpen && editingRecord && (
        <div className="aam-modal-overlay">
          <div className="aam-modal-card">
            <h3 className="aam-modal-title">✏️ Chỉnh Sửa Lượt Điểm Danh</h3>
            <div className="aam-modal-info-box">
              <p><strong>Thiếu nhi:</strong> {editingRecord.holyName} {editingRecord.fullName} ({editingRecord.studentId})</p>
              <p><strong>Lớp:</strong> {editingRecord.className}</p>
            </div>

            <form onSubmit={handleSubmitEdit}>
              <div className="aam-form-group">
                <label>Loại hoạt động (*):</label>
                <select
                  value={editForm.activityType}
                  onChange={(e) => setEditForm({ ...editForm, activityType: e.target.value })}
                  className="aam-modal-input"
                >
                  <option value="church">⛪ Đi Lễ Chúa Nhật</option>
                  <option value="catechism">📖 Học Giáo Lý</option>
                </select>
              </div>

              <div className="aam-form-group">
                <label>Ngày điểm danh (*):</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  className="aam-modal-input"
                  required
                />
              </div>

              <div className="aam-form-group">
                <label>Trạng thái / Giờ (*):</label>
                <input
                  type="text"
                  value={editForm.timeStatus}
                  onChange={(e) => setEditForm({ ...editForm, timeStatus: e.target.value })}
                  className="aam-modal-input"
                  required
                />
              </div>

              <div className="aam-form-group">
                <label>Người điểm danh (GLV/Admin):</label>
                <input
                  type="text"
                  value={editForm.catechist}
                  onChange={(e) => setEditForm({ ...editForm, catechist: e.target.value })}
                  className="aam-modal-input"
                />
              </div>

              <div className="aam-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isEditing}
                  className="aam-btn aam-btn-cancel"
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="aam-btn aam-btn-save"
                >
                  {isEditing ? '⏳ Đang lưu...' : '💾 Cập Nhật'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllAttendanceManager;